/**
 * 利用者の管理（管理者のみ）。
 *
 * 店舗の管理者が、利用者の招待・役割と上長の変更・担当品目の割り当て・削除を行う。
 * これまで SAKAMA が scripts/bootstrap-admin.mjs で行っていた作業を画面に移したもの。
 *
 * 書き込みはすべて Callable（functions/users.mjs）を通す。所属テナントのクレームは
 * Admin SDK でしか付けられず、担当品目は品目側と利用者側の両方を揃える必要があり、
 * 権限の変更は台帳に残すため。読み取りは firestore.rules の範囲で直接行う。
 *
 * 「今どの品目の担当か」は品目側の assigneeUids から引く。権限の判定（ルール・配信プロキシ）
 * が見ているのはこちらで、利用者側の assignedDishIds は写しにすぎない。
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { onSnapshot, orderBy, query } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useAuth } from "../auth";
import { dishTitle, useI18n } from "../lib/i18n";
import { ROLE_LABEL, type DishDoc, type InviteDoc, type Role, type UserDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const inviteUser = httpsCallable<{ email: string; role: Role; displayName: string | null }, { ok: boolean }>(
  functions,
  "inviteUser",
);
const cancelInvite = httpsCallable<{ email: string }, { ok: boolean }>(functions, "cancelInvite");
const updateUser = httpsCallable<
  { uid: string; role?: Role; supervisorUid?: string | null; displayName?: string },
  { ok: boolean }
>(functions, "updateUser");
const setUserDishes = httpsCallable<{ uid: string; dishIds: string[] }, { ok: boolean }>(functions, "setUserDishes");
const removeUser = httpsCallable<{ uid: string }, { ok: boolean }>(functions, "removeUser");

const ROLES: Role[] = ["admin", "chef", "supervisor", "worker"];

type UserRow = UserDoc & { uid: string };
type DishRow = { id: string; name: string; nameZh: string | null; assigneeUids: string[] };

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function Users() {
  const { user, profile, paths } = useAuth();
  const { t } = useI18n();
  const isAdmin = profile?.role === "admin";

  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [invites, setInvites] = useState<(InviteDoc & { id: string })[]>([]);
  const [dishes, setDishes] = useState<DishRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingDishesOf, setEditingDishesOf] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin || !paths) return;
    const offUsers = onSnapshot(
      paths.users(),
      (snap) => setUsers(snap.docs.map((d) => ({ uid: d.id, ...(d.data() as UserDoc) }))),
      (e) => setError(e.message),
    );
    const offInvites = onSnapshot(
      query(paths.invites(), orderBy("createdAt", "desc")),
      (snap) => setInvites(snap.docs.map((d) => ({ id: d.id, ...(d.data() as InviteDoc) }))),
      (e) => setError(e.message),
    );
    const offDishes = onSnapshot(
      query(paths.dishes(), orderBy("updatedAt", "desc")),
      (snap) =>
        setDishes(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as DishDoc) }))
            .filter((d) => !d.deletedAt) // 削除済みの品目は担当にしない
            .map((d) => ({ id: d.id, name: d.name, nameZh: d.nameZh ?? null, assigneeUids: d.assigneeUids ?? [] })),
        ),
      (e) => setError(e.message),
    );
    return () => {
      offUsers();
      offInvites();
      offDishes();
    };
  }, [isAdmin, paths]);

  const sorted = useMemo(() => {
    if (!users) return null;
    const order = (r: Role) => ROLES.indexOf(r);
    return [...users].sort(
      (a, b) => order(a.role) - order(b.role) || (a.displayName || a.email).localeCompare(b.displayName || b.email),
    );
  }, [users]);

  /** 上長に選べる人（作業者以外） */
  const supervisors = useMemo(() => (users ?? []).filter((u) => u.role !== "worker"), [users]);

  const dishesOf = (uid: string) => dishes.filter((d) => d.assigneeUids.includes(uid));

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      return true;
    } catch (e) {
      setError(message(e));
      return false;
    } finally {
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return (
      <main className="page">
        <p className="muted">{t("この画面は管理者のみ利用できます。")}</p>
      </main>
    );
  }

  return (
    <main className="page wide users-page">
      <div className="page-head">
        <h1>{t("利用者の管理")}</h1>
      </div>

      <InviteForm />

      {error && <p className="error">{error}</p>}

      {invites.length > 0 && (
        <section className="card">
          <h2>{t("招待中")}</h2>
          <p className="muted small">
            {t("招待したメールアドレスでログインすると、自動で利用者になります。")}
          </p>
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("メールアドレス")}</th>
                  <th>{t("役割")}</th>
                  <th>{t("招待した人")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      {inv.email}
                      {inv.displayName && <span className="muted small">（{inv.displayName}）</span>}
                    </td>
                    <td>{t(ROLE_LABEL[inv.role])}</td>
                    <td className="muted small">{inv.invitedByName ?? ""}</td>
                    <td>
                      <button
                        className="link"
                        disabled={busy !== null}
                        onClick={() => run(`cancel:${inv.id}`, () => cancelInvite({ email: inv.email }))}
                      >
                        {t("招待を取り消す")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="card">
        <h2>{t("利用者")}</h2>
        {!sorted ? (
          <p className="muted">{t("読み込み中…")}</p>
        ) : (
          <div className="tablewrap">
            <table className="table users-table">
              <thead>
                <tr>
                  <th>{t("名前")}</th>
                  <th>{t("役割")}</th>
                  <th>{t("上長")}</th>
                  <th>{t("担当品目")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((u) => {
                  const mine = dishesOf(u.uid);
                  const self = u.uid === user?.uid;
                  return (
                    <tr key={u.uid}>
                      <td>
                        {u.displayName || u.email}
                        {self && <span className="badge sub">{t("自分")}</span>}
                        <div className="muted small">{u.email}</div>
                      </td>
                      <td>
                        <select
                          aria-label={t("役割")}
                          value={u.role}
                          disabled={busy !== null}
                          onChange={(e) =>
                            run(`role:${u.uid}`, () => updateUser({ uid: u.uid, role: e.target.value as Role }))
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {t(ROLE_LABEL[r])}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          aria-label={t("上長")}
                          value={u.supervisorUid ?? ""}
                          disabled={busy !== null}
                          onChange={(e) =>
                            run(`sup:${u.uid}`, () =>
                              updateUser({ uid: u.uid, supervisorUid: e.target.value || null }),
                            )
                          }
                        >
                          <option value="">{t("なし")}</option>
                          {supervisors
                            .filter((s) => s.uid !== u.uid)
                            .map((s) => (
                              <option key={s.uid} value={s.uid}>
                                {s.displayName || s.email}
                              </option>
                            ))}
                        </select>
                      </td>
                      <td>
                        {u.role === "admin" || u.role === "chef" ? (
                          <span className="muted small">{t("全品目")}</span>
                        ) : (
                          <button
                            className="link"
                            disabled={busy !== null}
                            onClick={() => setEditingDishesOf(editingDishesOf === u.uid ? null : u.uid)}
                          >
                            {t("{n}品目", { n: mine.length })}
                          </button>
                        )}
                      </td>
                      <td>
                        {!self && (
                          <button
                            className="link danger-link"
                            disabled={busy !== null}
                            onClick={() => setRemoving(removing === u.uid ? null : u.uid)}
                          >
                            {t("削除")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">
          {t("管理者と料理長は全品目を見られます。上長と作業者は、担当に入れた品目だけを見られます。")}
        </p>
      </section>

      {removing && users && (
        <RemoveConfirm
          target={users.find((u) => u.uid === removing) ?? null}
          busy={busy !== null}
          onCancel={() => setRemoving(null)}
          onConfirm={async () => {
            if (await run(`remove:${removing}`, () => removeUser({ uid: removing }))) setRemoving(null);
          }}
        />
      )}

      {editingDishesOf && users && (
        <DishAssign
          key={editingDishesOf}
          target={users.find((u) => u.uid === editingDishesOf) ?? null}
          dishes={dishes}
          busy={busy !== null}
          onClose={() => setEditingDishesOf(null)}
          onSave={async (ids) => {
            if (await run(`dishes:${editingDishesOf}`, () => setUserDishes({ uid: editingDishesOf, dishIds: ids }))) {
              setEditingDishesOf(null);
            }
          }}
        />
      )}
    </main>
  );
}

/** 招待の入力欄 */
function InviteForm() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("worker");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await inviteUser({ email: email.trim(), role, displayName: displayName.trim() || null });
      setDone(email.trim());
      setEmail("");
      setDisplayName("");
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={onSubmit}>
      <h2>{t("利用者を招待")}</h2>
      <p className="muted small">
        {t("招待した人には、メールは届きません。この画面のアドレス（app.ichininmae.jp）を伝え、招待したメールアドレスでログインしてもらってください（Google アカウントか、ログイン画面のメールリンク）。")}
      </p>
      <label htmlFor="invite-email">{t("メールアドレス *")}</label>
      <input id="invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      <label htmlFor="invite-name">{t("表示名")}</label>
      <input
        id="invite-name"
        maxLength={60}
        value={displayName}
        placeholder={t("空欄ならログイン時の名前を使います")}
        onChange={(e) => setDisplayName(e.target.value)}
      />
      <label htmlFor="invite-role">{t("役割")}</label>
      <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {t(ROLE_LABEL[r])}
          </option>
        ))}
      </select>
      {error && <p className="error">{error}</p>}
      {done && <p className="ok small">{t("{email} を招待しました。", { email: done })}</p>}
      <div>
        <button type="submit" disabled={busy || !email.trim()}>
          {busy ? t("処理中…") : t("招待する")}
        </button>
      </div>
    </form>
  );
}

/** 担当品目の割り当て。チェックを付けた品目だけを見られるようになる */
function DishAssign({
  target,
  dishes,
  busy,
  onClose,
  onSave,
}: {
  target: UserRow | null;
  dishes: DishRow[];
  busy: boolean;
  onClose: () => void;
  onSave: (ids: string[]) => void;
}) {
  const { t, lang } = useI18n();
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(target ? dishes.filter((d) => d.assigneeUids.includes(target.uid)).map((d) => d.id) : []),
  );
  if (!target) return null;

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="card dish-assign">
      <h2>{t("{name} の担当品目", { name: target.displayName || target.email })}</h2>
      {dishes.length === 0 ? (
        <p className="muted">{t("品目がまだありません。")}</p>
      ) : (
        <ul className="checklist">
          {dishes.map((d) => (
            <li key={d.id}>
              <label className="check">
                <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                {dishTitle(d, lang)}
              </label>
            </li>
          ))}
        </ul>
      )}
      <div>
        <button disabled={busy} onClick={() => onSave([...selected])}>
          {busy ? t("保存中…") : t("保存する")}
        </button>{" "}
        <button type="button" className="link" onClick={onClose} disabled={busy}>
          {t("やめる")}
        </button>
      </div>
    </section>
  );
}

function RemoveConfirm({
  target,
  busy,
  onCancel,
  onConfirm,
}: {
  target: UserRow | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  if (!target) return null;
  return (
    <div className="card warn delete-dish">
      <p>
        <strong>{t("「{name}」を利用者から削除します。", { name: target.displayName || target.email })}</strong>
      </p>
      <p className="small">
        {t("削除すると、この人はすぐにマニュアルを開けなくなり、ログイン中の画面も使えなくなります。担当品目からも外れます。評価や操作の記録は残ります。もう一度使ってもらうときは、招待し直してください。")}
      </p>
      <button className="danger" disabled={busy} onClick={onConfirm}>
        {busy ? t("処理中…") : t("削除する")}
      </button>{" "}
      <button type="button" className="link" onClick={onCancel} disabled={busy}>
        {t("やめる")}
      </button>
    </div>
  );
}
